import { Fragment } from "react";
import {
  filingStatuses,
  businessFilingStatuses,
} from "../forms/selectOptionData";

/**
 * Reusable component for rendering filing summary content
 * Used by FilingDetailsModal, CpaCheckFiling, and any other place that needs to display filing details
 * 
 * @param {Object} filing - The filing object containing tax_situation
 * @param {boolean} showPrice - Whether to show the price section (default: true)
 * @param {string} priceLabel - Label for the price (default: "Total Filing Price")
 * @param {number|string} priceValue - Custom price value (default: filing.tax_situation.filing_price)
 */
function FilingSummaryContent({ 
  filing, 
  showPrice = true, 
  priceLabel = "Total Filing Price",
  priceValue = null 
}) {
  const tax_situation = filing?.tax_situation;
  const tax_details = tax_situation?.details || {};
  const services = Object.keys(tax_details);

  // Helper function to get label from value
  const getLabelFromValue = (value, options) => {
    const option = options.find((opt) => opt.value === value);
    return option ? option.label : value;
  };

  // Helper to format states array
  const formatStatesArray = (states) => {
    if (!states || !Array.isArray(states) || states.length === 0) return "None";
    return states.join(", ");
  };

  // Helper to get active income sources with counts
  const getIncomeSources = (situation) => {
    const sources = [];
    if (situation?.wtwoForm?.value) {
      const count = situation.wtwoForm.fields?.w2Count || 1;
      sources.push({ label: "W-2", count });
    }
    if (situation?.tenForm?.value) {
      const count = situation.tenForm.fields?.ten99Count || 1;
      sources.push({ label: "1099", count });
    }
    if (situation?.miscForm?.value) {
      const count = situation.miscForm.fields?.ten99Count || 1;
      sources.push({ label: "1099-MISC", count });
    }
    if (situation?.scheduleK?.value) {
      const count = situation.scheduleK.fields?.scheduleKCount || 1;
      sources.push({ label: "Schedule K-1", count });
    }
    if (situation?.stockOrDividends?.value) {
      const count = situation.stockOrDividends.fields?.stockOrDividendsCount || 1;
      sources.push({ label: "Stocks/Dividends", count });
    }
    if (situation?.rentalProperties?.value) {
      const count = situation.rentalProperties.fields?.rentalPropertiesCount || 1;
      sources.push({ label: "Rental Properties", count });
    }
    if (situation?.cryptoTransaction?.value) {
      const exchanges = situation.cryptoTransaction.fields?.exchanges || [];
      sources.push({ label: "Crypto", count: exchanges.length || 1 });
    }
    if (situation?.foreignIncome?.value) {
      const fields = situation.foreignIncome.fields || {};
      const count = (fields.foreignBankAccountsCount || 0) + 
                    (fields.foreignInvestmentAccountsCount || 0) + 
                    (fields.foreignRetirementAccountsCount || 0) + 
                    (fields.foreignRealEstatePropertiesCount || 0) + 
                    (fields.foreignOtherAssetsCount || 0) || 1;
      sources.push({ label: "Foreign Income", count });
    }
    return sources;
  };

  // Helper to get deductions/credits with counts
  const getDeductions = (situation) => {
    const deductions = [];
    if (situation?.esLoans?.value) {
      deductions.push({ label: "Student Loans", count: null });
    }
    if (situation?.cdcExpenses?.value) {
      const amount = situation.cdcExpenses.fields?.expensesTotalAmount;
      deductions.push({ label: "Child Care", count: amount ? `$${amount}` : null });
    }
    if (situation?.cdlmExpenses?.value) {
      deductions.push({ label: "Charitable/Medical", count: null });
    }
    if (situation?.healthInsurance?.value) {
      deductions.push({ label: "Health Insurance", count: null });
    }
    if (situation?.naturalDisaster?.value) {
      const count = situation.naturalDisaster.fields?.naturalDisasterCount || 1;
      deductions.push({ label: "Natural Disaster", count });
    }
    if (situation?.rcDistributions?.value) {
      deductions.push({ label: "Retirement", count: null });
    }
    if (situation?.propertiesAssets?.value) {
      const fields = situation.propertiesAssets.fields || {};
      const count = (fields.realEstateSoldCount || 0) + (fields.otherAssetsSoldCount || 0) || 1;
      deductions.push({ label: "Property Sales", count });
    }
    return deductions;
  };

  // Render a row item
  const renderRow = (label, value) => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        padding: "8px 0",
        borderBottom: "1px solid #e0e0e0",
        borderRadius: "0",
        fontSize: "14px",
      }}
    >
      <span style={{ color: "#666", fontWeight: "400" }}>{label}</span>
      <span style={{ color: "#1A2127", textAlign: "right", maxWidth: "60%", fontWeight: "300" }}>
        {value || "Not specified"}
      </span>
    </div>
  );

  // Render PTF details
  const renderPTF = (situation, background) => {
    const incomeSources = getIncomeSources(situation);
    const deductions = getDeductions(situation);

    return (
      <div style={{ marginBottom: "24px" }}>
        <h4 style={{ color: "#004789", marginBottom: "16px", borderBottom: "2px solid #004789", paddingBottom: "8px", borderRadius: "0" }}>
          Personal Filing Overview
        </h4>

        {/* Personal Information */}
        <div style={{ marginBottom: "16px" }}>
          <h5 style={{ color: "#666", marginBottom: "8px", fontSize: "14px" }}>Personal Information</h5>
          {renderRow("Name", `${background?.firstname || ""} ${background?.lastname || ""}`.trim())}
          {renderRow("Email", background?.email)}
          {renderRow("Phone", background?.phone)}
          {renderRow("Date of Birth", background?.dBirth)}
          {background?.addresses?.length > 0 && renderRow(
            "State of Residence",
            [...new Set(background.addresses.map(addr => addr.stateAddress).filter(Boolean))]
              .join(", ") || "Not specified"
          )}
        </div>

        {/* Tax Details */}
        <div style={{ marginBottom: "16px" }}>
          <h5 style={{ color: "#666", marginBottom: "8px", fontSize: "14px" }}>Tax Details</h5>
          {renderRow("Tax Year(s)", situation?.taxYears?.join(", "))}
          {renderRow("Filing Status", getLabelFromValue(situation?.filingStatus, filingStatuses()))}
          {renderRow("Dependents", situation?.haveDependents?.value 
            ? `Yes (${situation.haveDependents.fields?.dependentsCount || 1})` 
            : "No")}
          {renderRow(
            "Income States",
            (() => {
              const incomeStates = situation?.incomeStates;
              if (!incomeStates) return "None";
              if (typeof incomeStates === 'object' && !Array.isArray(incomeStates)) {
                const states = Object.values(incomeStates).flat().filter(Boolean);
                return states.length > 0 ? states.join(", ") : "None";
              }
              if (Array.isArray(incomeStates) && incomeStates.length > 0) {
                return incomeStates.join(", ");
              }
              return "None";
            })()
          )}
        </div>

        {/* Income Sources */}
        {incomeSources.length > 0 && (
          <div style={{ marginBottom: "16px" }}>
            <h5 style={{ color: "#666", marginBottom: "8px", fontSize: "14px" }}>Income Sources ({incomeSources.length})</h5>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {incomeSources.map((source) => (
                <span
                  key={source.label}
                  style={{
                    background: "#e8f4fd",
                    color: "#004789",
                    padding: "4px 12px",
                    borderRadius: "16px",
                    fontSize: "12px",
                  }}
                >
                  {source.label}
                  {source.count > 0   
                    ? ` (${source.count} ${
                        source.label === "Crypto"
                          ? source.count === 1 ? "Exchange" : "Exchanges"
                          : source.count === 1 ? "Form" : "Forms"
                      })`
                    : ""
                  }
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Deductions */}
        {deductions.length > 0 && (
          <div style={{ marginBottom: "16px" }}>
            <h5 style={{ color: "#666", marginBottom: "8px", fontSize: "14px" }}>Deductions & Credits ({deductions.length})</h5>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {deductions.map((deduction) => (
                <span
                  key={deduction.label}
                  style={{
                    background: "#f0f9e8",
                    color: "#2d6a1e",
                    padding: "4px 12px",
                    borderRadius: "16px",
                    fontSize: "12px",
                  }}
                >
                  {deduction.label}{deduction.count ? ` (${deduction.count})` : ""}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Business/LLC */}
        {situation?.llc_owner?.value && (
          <div style={{ marginBottom: "16px" }}>
            {renderRow("LLC/Business Owner", "Yes")}
            {situation.llc_owner.fields?.includeBusiness?.value && 
              renderRow("Include Business in Filing", "Yes")}
          </div>
        )}
      </div>
    );
  };

  // Render BTF details
  const renderBTF = (situation, background) => {
    return (
      <div style={{ marginBottom: "24px" }}>
        <h4 style={{ color: "#004789", marginBottom: "16px", borderBottom: "2px solid #004789", paddingBottom: "8px", borderRadius: "0" }}>
          Business Filing Overview
        </h4>

        {/* Business Information */}
        <div style={{ marginBottom: "16px" }}>
          <h5 style={{ color: "#666", marginBottom: "8px", fontSize: "14px" }}>Business Information</h5>
          {renderRow("Business Name", background?.businessName)}
          {renderRow("Year Established", background?.yearEstablished)}
          {renderRow("Number of Owners", background?.ownersNumber)}
          {background?.businessAddress && renderRow(
            "Address",
            [
              background.businessAddress.address,
              background.businessAddress.city,
              background.businessAddress.stateAddress
            ].filter(Boolean).join(", ")
          )}
          {background?.stateRegistration && renderRow(
            "State Registration",
            typeof background.stateRegistration === 'string' 
              ? background.stateRegistration
              : formatStatesArray(background.stateRegistration)
          )}
          {background?.foreignStateRegistration && renderRow(
            "Foreign State Registration",
            typeof background.foreignStateRegistration === 'string' 
              ? background.foreignStateRegistration
              : formatStatesArray(background.foreignStateRegistration)
          )}
        </div>

        {/* Tax Details */}
        <div style={{ marginBottom: "16px" }}>
          <h5 style={{ color: "#666", marginBottom: "8px", fontSize: "14px" }}>Tax Details</h5>
          {renderRow("Tax Year(s)", situation?.taxYears?.join(", "))}
          {renderRow("Business Type", getLabelFromValue(situation?.filingStatus, businessFilingStatuses()))}
          {renderRow("Ownership Changes", situation?.ownershipChanges?.value ? "Yes" : "No")}
          {renderRow("Business Closed/Merged", situation?.businessClosedMerged?.value ? "Yes" : "No")}
          {renderRow("Distributions Issued", background?.distributionDividendsIssued?.value ? "Yes" : "No")}
        </div>

        {/* Assets & Reports */}
        {(situation?.purchaseAssets?.value || situation?.stateAnnualReport?.value || background?.stateAnnualReports) && (
          <div style={{ marginBottom: "16px" }}>
            <h5 style={{ color: "#666", marginBottom: "8px", fontSize: "14px" }}>Additional Information</h5>
            {situation?.purchaseAssets?.value && renderRow(
              "Purchased Assets",
              `${situation.purchaseAssets.fields?.purchaseAssetsCount || 1} asset(s)`
            )}
            {(situation?.stateAnnualReport?.value || background?.stateAnnualReports) && renderRow(
              "State Annual Reports",
              typeof background?.stateAnnualReports === 'string'
                ? background.stateAnnualReports
                : formatStatesArray(background?.stateAnnualReports || situation.stateAnnualReport.fields?.stateAnnualReports)
            )}
          </div>
        )}
      </div>
    );
  };

  // Calculate price to display
  const displayPrice = priceValue !== null 
    ? priceValue 
    : tax_situation?.filing_price?.toFixed(2) || "0.00";

  return (
    <Fragment>
      {services.map((service) => {
        const situation = tax_details[service]?.situation || {};
        const background = tax_details[service]?.background || {};

        if (service === "PTF") {
          return <Fragment key={service}>{renderPTF(situation, background)}</Fragment>;
        }
        if (service === "BTF") {
          return <Fragment key={service}>{renderBTF(situation, background)}</Fragment>;
        }
        return null;
      })}

      {/* Audit Protection */}
      <div
        style={{
          marginTop: "16px",
          padding: "12px 0",
          borderBottom: "1px solid #e0e0e0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span style={{ fontWeight: "500", color: "#40464b", fontSize: "14px" }}>Audit Protection</span>
        <span style={{ fontWeight: "300", color: "#666b6f", fontSize: "14px" }}>
          {filing?.audit_protection ? "Yes" : "No"}
        </span>
      </div>

      {/* Price */}
      {showPrice && (
        <div
          style={{
            marginTop: "16px",
            padding: "16px",
            background: "#f7f8f8",
            borderRadius: "8px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span style={{ fontWeight: "600", color: "#1A2127" }}>{priceLabel}</span>
          <span style={{ fontWeight: "700", color: "#004789", fontSize: "18px" }}>
            ${displayPrice}
          </span>
        </div>
      )}
    </Fragment>
  );
}

export default FilingSummaryContent;

